import React, { useState } from "react";
import { KeywordCreditsWidget } from "@/components/KeywordCredits";
import ChangePlanDialog, { PendingChangeBanner } from "@/components/ChangePlanDialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, X, Sparkles, Loader2, Zap, Rocket, Crown, ExternalLink, Lock, Star, Store, Plus } from "lucide-react";

const TIERS = [
  {
    id: "creator", name: "Creator", icon: Zap, highlight: false, badge: null,
    monthly: 29, quarterly: 79.99, biannual: 179.99, yearly: 290, sub: "For solo creators & founders",
    features: [
      { text: "Unlimited 1-Click 30-Day Campaigns", included: true },
      { text: "Facebook & LinkedIn publishing", included: true },
      { text: "Branded AI image rendering", included: true },
      { text: "Automated webhook publishing", included: true },
      { text: "Up to 5 social accounts", included: true },
      { text: "Basic Keyword Ideas (Google Autocomplete Suggestions)", included: true },
      { text: "Google + Meta PPC campaign exports", included: true },
      { text: "Long-form SEO blog articles", included: false },
      { text: "Shopify catalog sync & auto-swap", included: false },
      { text: "AI Strategy Consultant", included: false },
    ],
  },
  {
    id: "growth", name: "E-commerce Growth", icon: Store, highlight: true, badge: "★ Most Popular",
    monthly: 79, quarterly: 213, biannual: 399, yearly: 790, sub: "For Shopify & online stores",
    features: [
      { text: "Everything in Creator", included: true },
      { text: "Shopify catalog sync", included: true },
      { text: "Inventory-aware auto-swap (out-of-stock protection)", included: true },
      { text: "AI Strategy Consultant (50 runs/mo)", included: true },
      { text: "Up to 10 social accounts", included: true },
      { text: "100 Monthly Exact Search Volume Lookups (DataForSEO)", included: true },
      { text: "Keywords to Facebook & LinkedIn draft builder", included: true },
      { text: "Daily SEO blogs to Shopify + blog page (1-3 mo campaigns)", included: true },
      { text: "Priority generation speed", included: true },
    ],
  },
  {
    id: "agency", name: "Agency", icon: Crown, highlight: false, badge: null,
    monthly: 199, quarterly: 537, biannual: 999, yearly: 1990, sub: "For agencies & teams",
    features: [
      { text: "Everything in E-commerce Growth", included: true },
      { text: "Unlimited AI Strategy Consultant", included: true },
      { text: "Unlimited social accounts", included: true },
      { text: "Team seats & custom brand profiles", included: true },
      { text: "500 Monthly Exact Search Volume Lookups (DataForSEO)", included: true },
      { text: "Keyword CSV export & team sharing", included: true },
      { text: "6-month daily blog campaigns", included: true },
      { text: "Multiple workspaces", included: true },
      { text: "Priority support", included: true },
    ],
  },
];

const NAMES = { creator: "Creator", growth: "E-commerce Growth", agency: "Agency", pro: "Creator (Legacy)" };

export default function Billing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState("");
  const [onboarding, setOnboarding] = useState(null);
  const [interval, setInterval] = useState("monthly");
  const [changeKey, setChangeKey] = useState(null);

  React.useEffect(() => {
    api.get("/billing/onboarding").then((r) => setOnboarding(r.data)).catch(() => {});
  }, []);

  const upgrade = async (lookupKey) => {
    setLoading(lookupKey);
    try {
      const res = await api.post("/billing/checkout", { origin_url: window.location.origin, lookup_key: lookupKey });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading("");
    }
  };

  const openPortal = async () => {
    setLoading("portal");
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal.");
      setLoading("");
    }
  };

  const switchToYearly = async () => {
    setLoading("switch-yearly");
    try {
      await api.post("/billing/change-plan", { lookup_key: `${plan}_yearly` });
      toast.success("Switched to annual billing — you just saved 2 months!");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch to annual.");
      setLoading("");
    }
  };

  const cancelPlan = async () => {
    setLoading("cancel");
    try {
      const r = await api.post("/billing/cancel");
      toast.success(r.data?.in_paid_trial
        ? "Trial cancelled — you won't be charged. You keep full access until it ends."
        : "Plan cancelled — you keep full access until the period ends.");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
      setLoading("");
    }
  };

  const resumePlan = async () => {
    setLoading("resume");
    try {
      await api.post("/billing/resume");
      toast.success("Welcome back — your plan will keep renewing.");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume.");
      setLoading("");
    }
  };

  const plan = user?.plan;
  const isLegacyPro = !!user?.is_legacy_pro;
  const isActive = ["creator", "growth", "agency", "pro"].includes(plan);
  const onMonthly = (user?.billing_interval || "monthly") === "monthly";
  const canSwitchYearly = isActive && !isLegacyPro && onMonthly && ["creator", "growth", "agency"].includes(plan);

  const ADDONS = [
    { id: "addon_accounts_monthly", name: "+5 Social Accounts", price: 10, icon: Zap,
      desc: "Connect more brands & profiles.", owned: user?.addon_accounts || 0, unit: "accounts" },
    { id: "addon_strategy_monthly", name: "+50 AI Strategy Runs", price: 19, icon: Sparkles,
      desc: "More AI Consultant runs each month.", owned: user?.addon_strategy_runs || 0, unit: "runs/mo" },
  ];

  const PlanCard = ({ id, name, badge, monthly, quarterly, biannual, yearly, sub, features, highlight, icon: Icon }) => {
    const priceMap = { monthly, quarterly, biannual, yearly };
    const cadenceMap = { monthly: "/mo", quarterly: "/3mo", biannual: "/6mo", yearly: "/yr" };
    const perMonth = { quarterly: quarterly / 3, biannual: biannual / 6 };
    const price = priceMap[interval];
    const cadence = cadenceMap[interval];
    const lookup = `${id}_${interval}`;
    const isCurrent = id === plan && isActive;
    return (
      <div className={`rounded-2xl border ${highlight ? "border-primary/50" : "border-border"} bg-card overflow-hidden flex flex-col`} data-testid={`plan-card-${id}`}>
        <div className="p-6 border-b border-border relative">
          {highlight && <div className="absolute top-0 right-0 h-24 w-24 bg-primary/15 blur-3xl" />}
          <div className="relative flex items-center justify-between">
            <div>
              <Badge className={`mb-3 ${highlight ? "bg-primary/15 text-primary border-primary/30" : "bg-secondary text-foreground border-border"}`}>
                {badge ? <><Star className="h-3.5 w-3.5 mr-1.5" /> {badge}</> : name}
              </Badge>
              <div className="text-sm font-semibold text-foreground mb-1">{name}</div>
              <div className="flex items-end gap-1.5">
                <span className="text-4xl font-display font-extrabold" data-testid={`price-${id}`}>${price}</span>
                <span className="text-muted-foreground mb-1.5 text-sm">{cadence}</span>
              </div>
              {interval === "yearly" && <p className="text-xs text-emerald-400 mt-1">2 months free</p>}
              {perMonth[interval] && <p className="text-xs text-emerald-400 mt-1">${perMonth[interval].toFixed(2)}/mo · billed {interval === "quarterly" ? "every 3 months" : "every 6 months"}</p>}
              <p className="text-xs text-muted-foreground mt-2">{sub}</p>
            </div>
            <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${highlight ? "bg-primary" : "bg-secondary"}`}>
              <Icon className={`h-6 w-6 ${highlight ? "text-white" : "text-foreground"}`} />
            </div>
          </div>
        </div>
        <div className="p-6 flex flex-col flex-1">
          <ul className="space-y-2.5 mb-6 flex-1">
            {features.map((f) => (
              <li key={f.text} className={`flex items-start gap-2.5 text-sm ${f.included ? "" : "text-muted-foreground/60"}`}>
                <span className={`h-4.5 w-4.5 rounded-full flex items-center justify-center shrink-0 mt-0.5 p-0.5 ${f.included ? "bg-emerald-500/20" : "bg-rose-500/15"}`}>
                  {f.included ? <Check className="h-3 w-3 text-emerald-400" /> : <X className="h-3 w-3 text-rose-400" />}
                </span>
                {f.text}
              </li>
            ))}
          </ul>
          {isCurrent ? (
            <Button disabled className="w-full h-11 bg-emerald-600/20 text-emerald-400 border border-emerald-500/40" data-testid={`plan-active-${id}`}>
              <Check className="h-4 w-4 mr-2" /> Current plan
            </Button>
          ) : (
            <Button data-testid={`checkout-${id}`} onClick={() => (isActive && user?.subscription_status === "active" ? setChangeKey(lookup) : upgrade(lookup))} disabled={!!loading}
              className={`w-full h-11 text-base ${highlight ? "bg-primary hover:bg-primary/90 text-white" : "border border-primary/40 text-primary bg-transparent hover:bg-primary/10"}`}>
              {loading === lookup ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Choose {name}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 max-w-6xl">
      <PendingChangeBanner />
      <ChangePlanDialog lookupKey={changeKey} onClose={() => setChangeKey(null)} onNoSubscription={upgrade} />
      <div className="text-center">
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight uppercase">Simple, transparent pricing</h1>
        <p className="text-muted-foreground mt-2">Autonomous visual merchandising for your brand — pick the plan that fits.</p>
      </div>

      <div className="flex items-center justify-center">
        <div className="inline-flex rounded-full border border-border bg-card p-1 flex-wrap justify-center" data-testid="billing-interval-toggle">
          <button data-testid="interval-monthly" onClick={() => setInterval("monthly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${interval === "monthly" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Monthly
          </button>
          <button data-testid="interval-quarterly" onClick={() => setInterval("quarterly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${interval === "quarterly" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            3-Month <span className="text-emerald-400">· save 10%</span>
          </button>
          <button data-testid="interval-biannual" onClick={() => setInterval("biannual")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${interval === "biannual" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            6-Month <span className="text-emerald-400">· save 16%</span>
          </button>
          <button data-testid="interval-yearly" onClick={() => setInterval("yearly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${interval === "yearly" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Yearly <span className="text-emerald-400">· 2 months free</span>
          </button>
        </div>
      </div>

      {isLegacyPro && (
        <div data-testid="legacy-pro-badge" className="mx-auto inline-flex items-center gap-3 rounded-xl border border-amber-400/40 bg-gradient-to-r from-amber-500/15 to-yellow-500/10 px-4 py-3">
          <div className="h-9 w-9 rounded-lg bg-amber-400/20 flex items-center justify-center shrink-0"><Crown className="h-5 w-5 text-amber-400" /></div>
          <div className="text-sm">
            <p className="font-semibold text-amber-300">VIP Legacy Access</p>
            <p className="text-xs text-amber-200/70">Grandfathered at your original rate with Creator features. Upgrade anytime for Shopify sync & the AI Consultant.</p>
          </div>
        </div>
      )}

      {isActive && !isLegacyPro && (
        <div data-testid="active-plan-banner" className="flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 max-w-lg mx-auto">
          <Check className="h-4 w-4 text-emerald-400 shrink-0" />
          <p className="text-sm text-foreground">Your <span className="font-semibold">{NAMES[plan] || plan}</span> subscription is active with full access.</p>
        </div>
      )}

      {isActive && user?.in_paid_trial && !user?.cancel_at_period_end && (
        <div data-testid="trial-cancel-banner" className="flex items-center justify-between gap-4 rounded-xl border border-primary/40 bg-primary/5 px-5 py-4 max-w-lg mx-auto flex-wrap">
          <div>
            <p className="text-sm font-semibold text-foreground">You're in your $1 trial{typeof user?.trial_days_left === "number" ? ` — ${user.trial_days_left} day${user.trial_days_left === 1 ? "" : "s"} left` : ""}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Cancel anytime before it ends and you won't be charged the renewal.</p>
          </div>
          <Button data-testid="cancel-trial-btn" onClick={cancelPlan} disabled={loading === "cancel"} variant="outline" className="border-rose-400/40 text-rose-300 hover:bg-rose-500/10 shrink-0">
            {loading === "cancel" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <X className="h-4 w-4 mr-2" />}
            Cancel trial
          </Button>
        </div>
      )}

      {isActive && user?.cancel_at_period_end && (
        <div data-testid="cancellation-scheduled-banner" className="flex items-center justify-between gap-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 max-w-lg mx-auto flex-wrap">
          <div>
            <p className="text-sm font-semibold text-amber-300">Cancellation scheduled</p>
            <p className="text-xs text-amber-200/70 mt-0.5">You keep full access until your current period ends — no further charges. Changed your mind?</p>
          </div>
          <Button data-testid="resume-plan-btn" onClick={resumePlan} disabled={loading === "resume"} className="bg-emerald-600 hover:bg-emerald-600/90 text-white shrink-0">
            {loading === "resume" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Resume plan
          </Button>
        </div>
      )}

      {canSwitchYearly && (
        <div data-testid="annual-upsell-banner" className="flex items-center justify-between gap-4 rounded-xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/12 to-teal-500/8 px-5 py-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-emerald-400" /></div>
            <div>
              <p className="text-sm font-semibold text-emerald-300">Save 2 months — switch to annual billing</p>
              <p className="text-xs text-emerald-200/70">You're on monthly. Pay yearly and get 2 months free, effective immediately (prorated).</p>
            </div>
          </div>
          <Button data-testid="switch-to-yearly-btn" onClick={switchToYearly} disabled={loading === "switch-yearly"} className="bg-emerald-600 hover:bg-emerald-600/90 text-white shrink-0">
            {loading === "switch-yearly" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Switch &amp; save
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {TIERS.map((p) => <PlanCard key={p.id} {...p} />)}
      </div>

      {isActive && (
        <div className="space-y-4" data-testid="addons-section">
          <div className="text-center">
            <h2 className="text-xl font-display font-bold tracking-tight uppercase">Power-ups</h2>
            <p className="text-sm text-muted-foreground mt-1">Standalone add-ons that stack onto any active plan.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {ADDONS.map((a) => (
              <div key={a.id} className="rounded-2xl border border-border bg-card p-6 flex items-center justify-between gap-4" data-testid={`addon-card-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center shrink-0"><a.icon className="h-5 w-5 text-foreground" /></div>
                  <div>
                    <div className="text-sm font-semibold">{a.name}</div>
                    <p className="text-xs text-muted-foreground">{a.desc}</p>
                    <div className="mt-1 text-lg font-display font-bold">${a.price}<span className="text-xs text-muted-foreground font-normal">/mo</span></div>
                    {a.owned > 0 && <Badge className="mt-1 bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]" data-testid={`addon-owned-${a.id}`}>Active: +{a.owned} {a.unit}</Badge>}
                  </div>
                </div>
                <Button data-testid={`addon-buy-${a.id}`} onClick={() => upgrade(a.id)} disabled={!!loading} variant="outline" className="border-primary/40 text-primary shrink-0">
                  {loading === a.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Add
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-center text-sm text-muted-foreground flex items-center justify-center gap-2" data-testid="pricing-trust">
        <Lock className="h-4 w-4" /> Instant access. Cancel anytime.
      </p>

      {isActive && (
        <div className="text-center flex items-center justify-center gap-3 flex-wrap">
          <Button data-testid="billing-portal-button" onClick={openPortal} disabled={loading === "portal"} variant="outline" className="border-border">
            {loading === "portal" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
            Manage billing
          </Button>
          {!user?.cancel_at_period_end && (
            <Button data-testid="billing-cancel-button" onClick={cancelPlan} disabled={loading === "cancel"} variant="ghost" className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
              {loading === "cancel" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <X className="h-4 w-4 mr-2" />}
              Cancel plan
            </Button>
          )}
        </div>
      )}

      {onboarding?.mode === "test" && onboarding?.url && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 max-w-md mx-auto">
          <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-1">
            <Rocket className="h-4 w-4" /> Test mode — no real charges
          </div>
          <p className="text-xs text-muted-foreground mb-3">You're in a Stripe sandbox. Claim your account to accept real payments and go live.</p>
          <Button data-testid="stripe-go-live-button" onClick={() => window.open(onboarding.url, "_blank", "noopener")}
            variant="outline" className="w-full border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
            Claim account &amp; go live <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}
      <div className="mt-8 max-w-xl mx-auto" data-testid="billing-keyword-pack"><KeywordCreditsWidget /></div>
    </div>
  );
}
